import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, Delete, Loader2, Flag } from "lucide-react";

/**
 * SupervisorPinModal – prompts for a 6-digit supervisor PIN and verifies it
 * against POST /api/auth/verify-supervisor-pin. On success, calls onVerified
 * with `({ name, role }, note)` and closes. Auto-submits as soon as 6 digits
 * are entered.
 *
 * Pass `noteLabel="Comment (flags this record)"` to show an optional textarea
 * above the numpad. Text entered there is passed as the second arg to
 * `onVerified`. The parent decides what to do with it.
 */
export default function SupervisorPinModal({
  open,
  onClose,
  onVerified,
  title = "Supervisor PIN required",
  description = "Enter a supervisor PIN to authorise this change.",
  roles, // optional: e.g. ["admin"] or ["admin","dispatcher"]
  noteLabel, // optional: enables comment textarea
  notePlaceholder = "Optional — leave blank to sign off cleanly",
}) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) {
      setPin("");
      setBusy(false);
      setNote("");
    }
  }, [open]);

  const submit = async (value) => {
    if (busy) return;
    if (!value || value.length < 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-supervisor-pin", {
        pin: value,
        roles,
      });
      onVerified?.(data, note.trim(), value);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Incorrect PIN");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const press = (d) => {
    if (busy) return;
    const next = (pin + d).slice(0, 8);
    setPin(next);
    if (next.length === 6) submit(next);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="supervisor-pin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-stone-600 -mt-2">{description}</div>

        {noteLabel && (
          <div className="mt-1">
            <Label className="text-xs uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
              <Flag className="h-3.5 w-3.5 text-amber-600" /> {noteLabel}
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={notePlaceholder}
              rows={2}
              className="mt-1 resize-none"
              data-testid="supervisor-pin-note"
            />
            {note.trim() && (
              <div className="text-[11px] text-amber-700 mt-1 font-semibold">
                This record will be flagged for review.
              </div>
            )}
          </div>
        )}

        <div
          className="flex justify-center gap-3 my-2"
          data-testid="supervisor-pin-display"
        >
          {["d0", "d1", "d2", "d3", "d4", "d5"].map((id, i) => (
            <div
              key={id}
              className={`h-3.5 w-3.5 rounded-full border-2 ${
                pin.length > i ? "bg-primary border-primary" : "border-stone-300"
              }`}
            />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2" data-testid="supervisor-pin-numpad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => press(String(n))}
              disabled={busy}
              data-testid={`sup-pin-key-${n}`}
              className="h-14 rounded-lg bg-white border border-border text-xl font-display font-bold hover:bg-stone-50 active:scale-95 transition"
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPin("")}
            data-testid="sup-pin-clear"
            className="h-14 rounded-lg bg-stone-100 hover:bg-stone-200 text-xs font-semibold uppercase tracking-wider"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => press("0")}
            disabled={busy}
            data-testid="sup-pin-key-0"
            className="h-14 rounded-lg bg-white border border-border text-xl font-display font-bold hover:bg-stone-50 active:scale-95 transition"
          >
            0
          </button>
          <button
            type="button"
            onClick={() => setPin((p) => p.slice(0, -1))}
            data-testid="sup-pin-backspace"
            className="h-14 rounded-lg bg-stone-100 hover:bg-stone-200 flex items-center justify-center"
          >
            <Delete className="h-5 w-5" />
          </button>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => submit(pin)}
            disabled={busy || pin.length < 6}
            data-testid="supervisor-pin-submit"
            className="h-11 px-6"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
